import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { useRooms, useDeleteRoom } from "../../../../../hooks/query/useRooms";
import { Room } from "../../../../../interfaces/RoomInterface";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { RoomCreateEdit } from "./RoomCreateEdit";

// eslint-disable-next-line import/prefer-default-export
export function RoomList(): React.ReactNode {
  const { data: rooms = [], isLoading } = useRooms();
  const { mutate: deleteRoom } = useDeleteRoom();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<number | null>(null);
  const [isCreateEditOpen, setIsCreateEditOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);

  const handleDelete = () => {
    if (deleteDialogOpen) {
      deleteRoom(deleteDialogOpen, {
        onSuccess: () => {
          setDeleteDialogOpen(null);
        }
      });
    }
  };

  const handleCreateNew = () => {
    setEditingRoom(null);
    setIsCreateEditOpen(true);
  };

  const handleEdit = (room: Room) => {
    setEditingRoom(room);
    setIsCreateEditOpen(true);
  };

  const handleCreateEditSuccess = () => {
    setIsCreateEditOpen(false);
    setEditingRoom(null);
  };

  const handleCreateEditClose = () => {
    setIsCreateEditOpen(false);
    setEditingRoom(null);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%"
        }}
      >
        <Typography fontWeight="500">Manage Rooms</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew}>
          Add Room
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "25%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Name
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Code
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Ward
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Type
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                <Typography fontWeight="600" fontSize="13px">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          {rooms.length > 0 && (
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id} sx={{ height: "48px" }}>
                  <TableCell>
                    <Typography fontSize="13px">{room.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px">{room.code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px" sx={{ textTransform: "capitalize" }}>
                      {room.ward?.name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="13px" sx={{ textTransform: "capitalize" }}>
                      {room.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                      <EditIcon
                        sx={{ cursor: "pointer", fontSize: "16px", color: "rgba(0, 0, 0, 0.54)" }}
                        onClick={() => handleEdit(room)}
                      />
                      <DeleteIcon
                        sx={{ cursor: "pointer", fontSize: "16px", color: "red" }}
                        onClick={() => setDeleteDialogOpen(room.id)}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Room"
          description="Are you sure you want to delete this room?"
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(null)}
          readMode={false}
        />
      )}

      {isCreateEditOpen && (
        <RoomCreateEdit
          onClose={handleCreateEditClose}
          onSuccess={handleCreateEditSuccess}
          room={editingRoom}
        />
      )}
    </Box>
  );
}
